import { useCallback, useMemo, useState } from 'react';
import { createContext, useContext } from 'use-context-selector';

import { CameraPermissionStatus } from '~types';

const initialContextValues = {
  latestPermissionStatus: null as CameraPermissionStatus,
  handleSetLatestPermissionStatus: (_status: CameraPermissionStatus) => {
    return;
  },
  errorOnRequestCameraPermission: null as Error | null,
  requestCameraPermission: async (): Promise<CameraPermissionStatus> => {
    return null;
  },
};

const CameraPermissionContext = createContext(initialContextValues);

type Props = {
  children: React.ReactNode;
};

export const CameraPermissionProvider: React.FC<Props> = ({ children }) => {
  const [cameraPermissionStatus, setCameraPermissionStatus] =
    useState<CameraPermissionStatus>(null);
  const [error, setError] = useState<Error | null>(null);

  const onSetCameraPermissionStatus = useCallback(
    (status: CameraPermissionStatus) => {
      setCameraPermissionStatus(status);
    },
    [],
  );

  // Request camera permission function
  const requestCameraPermission =
    useCallback(async (): Promise<CameraPermissionStatus> => {
      try {
        // Attempt to access the camera
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            aspectRatio: { ideal: 1.7777777778 }, // 16:9 aspect ratio
          },
        });

        // Stop the stream immediately to avoid using resources unnecessarily
        stream.getTracks().forEach((track) => track.stop());
        setError(null); // Clear any previous error

        return 'granted';
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Camera loading error:', err); // For debugging purposes

        setError(err as Error);
        // Handle specific error cases based on the error type
        if ((err as Error).name === 'NotAllowedError') {
          return 'denied';
        }
        return 'error';
      }
    }, []);

  const providerValues = useMemo(
    () => ({
      latestPermissionStatus: cameraPermissionStatus,
      handleSetLatestPermissionStatus: onSetCameraPermissionStatus,
      errorOnRequestCameraPermission: error,
      requestCameraPermission,
    }),
    [
      cameraPermissionStatus,
      error,
      onSetCameraPermissionStatus,
      requestCameraPermission,
    ],
  );

  return (
    <CameraPermissionContext.Provider value={providerValues}>
      {children}
    </CameraPermissionContext.Provider>
  );
};

export const useCameraPermission = () => useContext(CameraPermissionContext);
